import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";

import DashboardLayout from "../../../components/layout/DashboardLayout";
import AceAvatar from "../../../components/shared/AceAvatar";
import { db } from "../../../firebase/firebase";

function normalizeStatus(status) {
  const value = String(status || "").toLowerCase();

  if (value === "neu" || value === "open" || value === "offen") return "open";
  return "done";
}

function getStatusLabel(status) {
  return normalizeStatus(status) === "open" ? "Offen" : "Erledigt";
}

function StatusPill({ status }) {
  const normalized = normalizeStatus(status);
  const map = {
    open: "is-open",
    done: "is-done",
  };

  return (
    <span className={`ac-feedback-statuspill ${map[normalized] || ""}`}>
      {getStatusLabel(status)}
    </span>
  );
}

function RatingPill({ value }) {
  const rating = Number(value || 0);
  let tone = "is-green";
  let label = `${rating}/5`;

  if (rating <= 2) tone = "is-orange";
  else if (rating === 3) tone = "is-blue";

  return <span className={`ac-feedback-tonepill ${tone}`}>{label}</span>;
}

function parseCreatedAt(value) {
  if (!value) return null;
  if (value?.toDate && typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDateTime(value) {
  const parsed = parseCreatedAt(value);
  if (!parsed) return "—";

  return parsed.toLocaleString("de-AT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function FeedbackReplyPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const ref = doc(db, "websiteFeedback", id);
        const snap = await getDoc(ref);

        if (!mounted) return;

        if (snap.exists()) {
          const data = { id: snap.id, ...snap.data() };
          setFeedback(data);
          setReply(data.answerText || "");
        } else {
          setFeedback(null);
        }
      } catch (error) {
        console.error("Feedback konnte nicht geladen werden:", error);
        if (!mounted) return;
        setFeedback(null);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [id]);

  const quickReplies = useMemo(() => {
    if (!feedback) {
      return [
        "Danke für deine Rückmeldung. Wir nehmen dein Anliegen ernst und sehen es uns genau an.",
        "Danke für den Hinweis. Wir prüfen das intern und melden uns bei Bedarf mit einem Update.",
      ];
    }

    const base = [
      "Danke für deine Rückmeldung. Wir nehmen dein Anliegen ernst und sehen es uns genau an.",
      "Danke für den Hinweis. Wir prüfen das intern und melden uns bei Bedarf mit einem Update.",
    ];

    if (Number(feedback.rating || 0) <= 2) {
      return [
        "Danke für deine ehrliche Rückmeldung. Wir schauen uns den beschriebenen Punkt genau an.",
        "Es ist wichtig, dass solche Rückmeldungen sichtbar werden. Danke, dass du dir die Zeit genommen hast.",
        ...base,
      ];
    }

    return base;
  }, [feedback]);

  async function handleSend() {
    if (!reply.trim()) {
      alert("Bitte schreibe zuerst eine Antwort.");
      return;
    }

    try {
      setSaving(true);

      await updateDoc(doc(db, "websiteFeedback", id), {
        status: "erledigt",
        answerText: reply.trim(),
        answeredAt: new Date().toISOString(),
        answeredBy: "Admin",
      });

      navigate("/feedback");
    } catch (error) {
      console.error("Antwort konnte nicht gespeichert werden:", error);
      alert("Antwort konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <DashboardLayout title="Feedback">
        <div className="ac-soft-panel ac-feedback-block">
          <div className="text-lg font-extrabold text-ac-text">
            Lade Feedback...
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!feedback) {
    return (
      <DashboardLayout title="Feedback">
        <div className="ac-soft-panel ac-feedback-block">
          <div className="text-lg font-extrabold text-ac-text">
            Feedback nicht gefunden
          </div>
          <button
            type="button"
            onClick={() => navigate("/feedback")}
            className="mt-4 ac-ghostbtn"
          >
            Zurück zur Übersicht
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const isDone = normalizeStatus(feedback.status) === "done";
  const aceVariant =
    Number(feedback.rating || 0) <= 2 ? "stress1" : "ruhig2";

  return (
    <DashboardLayout title="Feedback">
      <div className="space-y-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="text-xl font-extrabold text-ac-text">Feedback</div>

          <button
            type="button"
            onClick={() => navigate("/feedback")}
            className="ac-ghostbtn"
          >
            ← Übersicht
          </button>
        </div>

        <section className="ac-soft-panel ac-feedback-hero">
          <div className="ac-feedback-hero-grid">
            <div>
              <h2 className="ac-feedback-hero-title">
                Rückmeldung von {feedback.name || "Unbekannt"}
              </h2>
              <p className="ac-feedback-hero-text">
                Hier siehst du die vollständige Meldung, den Kontext dazu und kannst
                direkt eine passende Antwort formulieren.
              </p>
            </div>

            <div className="ac-feedback-hero-side">
              <div className="ac-feedback-acebox">
                <AceAvatar variant={aceVariant} size={56} />
                <div className="ac-feedback-acebubble">
                  Ace sagt: Antworte klar, ruhig und konkret — genau das schafft Vertrauen.
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="ac-feedback-replylayout">
          <div className="ac-feedback-replymain">
            <div className="ac-soft-panel ac-feedback-panelcard">
              <div className="ac-feedback-panelhead">
                <div>
                  <div className="ac-feedback-paneltitle">Originalmeldung</div>
                  <div className="ac-feedback-panelsubtitle">
                    Vollständige Rückmeldung der Nutzerin oder des Nutzers
                  </div>
                </div>

                <StatusPill status={feedback.status} />
              </div>

              <div className="ac-feedback-messagecard">
                <div className="ac-feedback-messagequote">„{feedback.message || "—"}“</div>

                <div className="ac-feedback-chiprow">
                  <RatingPill value={feedback.rating} />
                </div>
              </div>
            </div>

            <div className="ac-soft-panel ac-feedback-panelcard">
              <div className="ac-feedback-panelhead">
                <div>
                  <div className="ac-feedback-paneltitle">
                    {isDone ? "Gespeicherte Antwort" : "Antwort verfassen"}
                  </div>
                  <div className="ac-feedback-panelsubtitle">
                    Formuliere eine klare, hilfreiche und wertschätzende Antwort
                  </div>
                </div>
              </div>

              {!isDone && (
                <div className="ac-feedback-helpergrid">
                  {quickReplies.map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setReply(item)}
                      className={`ac-feedback-helperbtn ${reply === item ? "is-active" : ""}`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              )}

              <div className="ac-feedback-field mt-5">
                <label className="ac-feedback-label">Antwort</label>
                <textarea
                  rows={7}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Antwort schreiben..."
                  className="ac-feedback-textarea ac-feedback-textarea-reply"
                  disabled={isDone}
                />
              </div>

              <div className="ac-feedback-actions-row ac-feedback-actions-row-split">
                <button
                  type="button"
                  onClick={() => navigate("/feedback")}
                  className="ac-ghostbtn ac-feedback-smallbtn"
                >
                  Zurück zur Übersicht
                </button>

                {!isDone && (
                  <button
                    type="button"
                    onClick={handleSend}
                    className="ac-primarybtn ac-feedback-smallbtn"
                    disabled={saving}
                  >
                    {saving ? "Speichert..." : "Antwort senden"}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="ac-feedback-replyside">
            <div className="ac-soft-panel ac-feedback-panelcard">
              <div className="ac-feedback-paneltitle">Kontext</div>

              <div className="ac-feedback-contextlist">
                <div className="ac-feedback-contextitem">
                  <div className="ac-feedback-contextlabel">Name</div>
                  <div className="ac-feedback-contextvalue">{feedback.name || "—"}</div>
                </div>

                <div className="ac-feedback-contextitem">
                  <div className="ac-feedback-contextlabel">Rolle</div>
                  <div className="ac-feedback-contextvalue">{feedback.role || "—"}</div>
                </div>

                <div className="ac-feedback-contextitem">
                  <div className="ac-feedback-contextlabel">Seite</div>
                  <div className="ac-feedback-contextvalue">{feedback.page || "—"}</div>
                </div>

                <div className="ac-feedback-contextitem">
                  <div className="ac-feedback-contextlabel">Bewertung</div>
                  <div className="ac-feedback-contextvalue">{feedback.rating || "—"}/5</div>
                </div>

                <div className="ac-feedback-contextitem">
                  <div className="ac-feedback-contextlabel">Datum</div>
                  <div className="ac-feedback-contextvalue">{formatDateTime(feedback.createdAt)}</div>
                </div>

                <div className="ac-feedback-contextitem">
                  <div className="ac-feedback-contextlabel">Feedback-ID</div>
                  <div className="ac-feedback-contextvalue">{feedback.id}</div>
                </div>

                <div className="ac-feedback-contextitem">
                  <div className="ac-feedback-contextlabel">Status</div>
                  <div className="ac-feedback-contextvalue">
                    {getStatusLabel(feedback.status)}
                  </div>
                </div>

                {feedback.answeredAt && (
                  <div className="ac-feedback-contextitem">
                    <div className="ac-feedback-contextlabel">Beantwortet</div>
                    <div className="ac-feedback-contextvalue">
                      {formatDateTime(feedback.answeredAt)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="ac-soft-panel ac-feedback-panelcard">
              <div className="ac-feedback-paneltitle">Schnelle Einordnung</div>
              <div className="ac-feedback-panelsubtitle">
                Kleine Hilfe für die Priorisierung im Admin-Panel
              </div>

              <div className="ac-feedback-note-box">
                {Number(feedback.rating || 0) <= 2
                  ? "Diese Rückmeldung wirkt eher kritisch und sollte nicht zu lange offen bleiben."
                  : "Diese Rückmeldung ist eher ruhig formuliert, aber trotzdem wertvoll für die Weiterentwicklung."}
              </div>
            </div>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}